import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./profileEditorialTabs.css";

// Mapa de rotas para cada tipo editorial
const EDITORIAL_FEED_ROUTES = {
  tudo: "/feeds",
  facebook: "/feeds",
  tiktok: "/tiktok",
  youtube: "/youtube",
  reels: "/reels",
  shorts: "/shorts",
  netflix: "/netflix",
  gaming: "/gaming",
  apkstore: "/apk-store",
  audio: "/audio",
  spotify: "/spotify",
  live_audio: "/live-audio",
  live_streaming: "/live-stream",
  movie_portal: "/movie-portal",
  video_bank: "/video-bank",
  threads: "/threads",
  gif: "/gif",
  sticker: "/sticker",
  cnn: "/cnn",
  blog: "/blog",
  podcast: "/podcast",
  portfolio: "/portfolio",
  library: "/library",
  ebook: "/ebook",
  pdf: "/pdf",
  course: "/course",
  quiz: "/quiz",
  tutorial: "/tutorial",
  estudent: "/estudent",
  school: "/school",
  voting: "/voting",
  esports: "/esports",
  game_review: "/game-review",
  eshop: "/eshop",
  aliexpress: "/aliexpress",
  restaurant: "/restaurant",
  food_delivery: "/food-delivery",
  booking: "/booking",
  airbnb: "/airbnb",
  rental: "/rental",
  uber: "/uber",
  jobservice: "/job-service",
  event: "/event",
  classified: "/classified",
  medical: "/medical",
  ong: "/ong",
  crowdfunding: "/crowdfunding",
  community: "/community",
  petition: "/petition",
  travel: "/travel",
  car: "/car",
  yoga: "/yoga",
  exercise: "/exercise",
  recipe: "/recipe",
  fashion: "/fashion",
  beauty: "/beauty",
};

/**
 * ============================================
 * PROFILE EDITORIAL TABS COMPONENT
 * ============================================
 * 
 * Renderiza abas dinâmicas de tipos editoriais
 * para filtrar posts do perfil do usuário
 * 
 * Features:
 * - Abas dinâmicas carregadas da API
 * - Suporte a "Mais" para abas excedentes
 * - Responsivo (desktop/mobile)
 * - Scroll horizontal em mobile
 * - Badges com contagem de posts
 */

export default function ProfileEditorialTabs({
  userId,
  profileUser,
  selectedType,
  onTypeChange,
  posts,
}) {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const [allTypes, setAllTypes] = useState([]); // Todos os tipos disponíveis
  const [userTypes, setUserTypes] = useState([]); // Tipos que o usuário tem conteúdo
  const [counts, setCounts] = useState({}); // Contagem por tipo
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [visibleTabsCount, setVisibleTabsCount] = useState(6); // Quantas abas mostrar antes de "Mais"
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabsContainerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const moreButtonRef = useRef(null);

  // Carregar tipos editoriais disponíveis
  useEffect(() => {
    fetchEditorialTypes();
  }, []);

  // Carregar tipos que o usuário tem
  useEffect(() => {
    if (userId) {
      fetchUserEditorialTypes();
      fetchCounts();
    }
  }, [userId]);

  // Calcular contagem se posts forem fornecidos
  useEffect(() => {
    if (posts && posts.length > 0) {
      const newCounts = { tudo: posts.length };
      posts.forEach((post) => {
        const type = post.editorialType || "facebook";
        newCounts[type] = (newCounts[type] || 0) + 1;
      });
      setCounts(newCounts);
    }
  }, [posts]);

  // Responder a mudanças de tamanho para ajustar visibilidade das abas
  useEffect(() => {
    const handleResize = () => {
      adjustVisibleTabsCount();
      checkScrollButtons();
    };

    window.addEventListener("resize", handleResize);
    adjustVisibleTabsCount(); // Chamar uma vez no mount
    checkScrollButtons();

    return () => window.removeEventListener("resize", handleResize);
  }, [userTypes]);

  // Verificar se deve mostrar botões de navegação
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  // Scroll para esquerda
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
      setTimeout(checkScrollButtons, 300);
    }
  };

  // Scroll para direita
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
      setTimeout(checkScrollButtons, 300);
    }
  };

  /**
   * Ajustar quantas abas mostrar baseado no tamanho da tela
   */
  const adjustVisibleTabsCount = () => {
    if (window.innerWidth < 640) {
      // Mobile
      setVisibleTabsCount(3);
    } else if (window.innerWidth < 1024) {
      // Tablet
      setVisibleTabsCount(4);
    } else {
      // Desktop
      setVisibleTabsCount(6);
    }
  };

  /**
   * Buscar todos os tipos editoriais
   */
  const fetchEditorialTypes = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/editorial/types`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (data.status === "success" && data.data) {
        setAllTypes(data.data);
      } else if (Array.isArray(data)) {
        // Fallback para resposta anterior
        setAllTypes(data);
      }
    } catch (error) {
      console.error("Erro ao buscar tipos editoriais:", error);
    }
  };

  /**
   * Buscar tipos que o usuário tem conteúdo
   */
  const fetchUserEditorialTypes = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/editorial/user-types/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (data.status === "success" && data.data) {
        setUserTypes(data.data);
      }
    } catch (error) {
      console.error("Erro ao buscar tipos do usuário:", error);
    }
  };

  /**
   * Buscar contagem de posts por tipo
   */
  const fetchCounts = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/editorial/profile-counts/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (data.status === "success" && data.data) {
        setCounts(data.data);
      }
    } catch (error) {
      console.error("Erro ao buscar contagem de posts:", error);
    }
  };

  /**
   * Renderizar ícone baseado no tipo
   */
  const getTabIcon = (type) => {
    if (!type.icon) {
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
        </svg>
      );
    }

    // Se o ícone é uma string, usar emoji ou SVG simples
    if (typeof type.icon === "string") {
      const iconMap = {
        grid: "🔲",
        home: "🏠",
        map: "🗺",
        shopping_bag: "🛍",
        store: "🏪",
        utensils: "🍴",
        book: "📖",
        school: "🎓",
        briefcase: "💼",
        people: "👥",
        movie: "🎬",
        gamepad: "🎮",
        music: "🎵",
        news: "📰",
        code: "💻",
        heart: "❤️",
      };
      return iconMap[type.icon] || "📌";
    }

    return type.icon;
  };

  /**
   * Manipular clique na aba
   * Sempre redireciona ao feed correspondente, filtrando pelo dono do perfil
   */
  const handleTabClick = (typeId) => {
    const feedRoute = EDITORIAL_FEED_ROUTES[typeId] || "/feeds";

    // Ajustar seleção local (mantém experiência no perfil)
    onTypeChange(typeId);

    // Redirecionar ao feed com filtro de usuário
    navigate(feedRoute, {
      state: {
        filterByUser: userId,
        filterByUsername:
          profileUser?.username ||
          profileUser?.first_name ||
          profileUser?.name ||
          "usuário",
        initialEditorialType: typeId,
        fromProfile: true,
      },
    });

    setShowMoreMenu(false);
  };

  // Se nenhum tipo do usuário, não mostrar abas
  if (userTypes.length === 0) {
    return null;
  }

  // Abas visíveis no scroll normal
  const visibleTabs = userTypes.slice(0, visibleTabsCount);

  // Abas no menu "Mais"
  const hiddenTabs = userTypes.slice(visibleTabsCount);

  return (
    <div className="profile_editorial_tabs_container">
      {/* Botão Esquerda */}
      {showLeftArrow && (
        <button
          className="editorial_tabs_nav_btn editorial_tabs_nav_left"
          onClick={scrollLeft}
          title="Rolar para esquerda"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
      )}

      {/* Botão Direita */}
      {showRightArrow && (
        <button
          className="editorial_tabs_nav_btn editorial_tabs_nav_right"
          onClick={scrollRight}
          title="Rolar para direita"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
      )}

      <div className="profile_editorial_tabs" ref={tabsContainerRef}>
        <div className="editorial_tabs_scroll" ref={scrollContainerRef} onScroll={checkScrollButtons}>
          {/* Abas visíveis */}
          {visibleTabs.map((type) => (
            <button
              key={type.id}
              className={`editorial_tab ${selectedType === type.id ? "active" : ""}`}
              onClick={() => handleTabClick(type.id)}
              style={{
                "--tab-color": type.color,
              }}
              title={`${type.description}\n\n💡 Dica: Ctrl+Clique para abrir no feed ${type.name}`}
            >
              <span className="editorial_tab_icon">
                {getTabIcon(type)}
              </span>
              <span className="editorial_tab_name">{type.name}</span>
              {counts[type.id] > 0 && (
                <span className="editorial_tab_count">
                  {counts[type.id]}
                </span>
              )}
            </button>
          ))}

          {/* Botão "Mais" se houver abas escondidas */}
          {hiddenTabs.length > 0 && (
            <div className="editorial_tab_more_wrapper">
              <button
                ref={moreButtonRef}
                className={`editorial_tab editorial_tab_more ${
                  showMoreMenu ? "active" : ""
                }`}
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                title="Mostrar mais abas"
              >
                <span className="editorial_tab_icon">⋯</span>
                <span className="editorial_tab_name">Mais</span>
              </button>

              {/* Menu dropdown de abas escondidas */}
              {showMoreMenu && (
                <div className="editorial_tabs_more_menu">
                  {hiddenTabs.map((type) => (
                    <button
                      key={type.id}
                      className={`editorial_tab_more_item ${
                        selectedType === type.id ? "active" : ""
                      }`}
                      onClick={() => handleTabClick(type.id)}
                      style={{
                        "--tab-color": type.color,
                      }}
                      title={`${type.description}\n\nClique para abrir o feed ${type.name}`}
                    >
                      <span className="editorial_tab_icon">
                        {getTabIcon(type)}
                      </span>
                      <span className="editorial_tab_name">{type.name}</span>
                      {counts[type.id] > 0 && (
                        <span className="editorial_tab_count">
                          {counts[type.id]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fechar menu ao clicar fora */}
      {showMoreMenu && (
        <div
          className="editorial_tabs_backdrop"
          onClick={() => setShowMoreMenu(false)}
        />
      )}
    </div>
  );
}
